'use client'
import React from 'react'
import { X } from 'lucide-react'
import { IShippingDetails } from '@/lib/interfaces/ordering'
import { Language } from '@/lib/interfaces/interfaces'
import AddressAutocomplete, { getAddressLabel, SelectedAddressPlace } from '@/components/places/AddressAutocomplete'

interface ShippingDetailsProps {
  shippingDetails: IShippingDetails
  setShippingDetails: React.Dispatch<React.SetStateAction<IShippingDetails>>
  toggleCart: () => void
  errors?: { [key: string]: string }
  language: Language
  proximity?: { lat: number; lng: number }
}

// Objeto de traducciones para cada idioma
const translations = {
  es: {
    shippingTitle: 'Datos de Envío',
    closeCart: 'Cerrar carrito',
    firstName: 'Nombre',
    lastName: 'Apellido',
    address: 'Dirección',
    address2: 'Dirección 2 (opcional)',
    city: 'Ciudad',
    postalCode: 'Código Postal',
    provinceState: 'Provincia/Estado',
    phone: 'Teléfono',
    additionalInfo: 'Información Adicional',
    searching: 'Buscando…',
    noAddressResults: 'Sin resultados'
  },
  en: {
    shippingTitle: 'Shipping Details',
    closeCart: 'Close cart',
    firstName: 'First Name',
    lastName: 'Last Name',
    address: 'Address',
    address2: 'Address 2 (optional)',
    city: 'City',
    postalCode: 'Postal Code',
    provinceState: 'Province/State',
    phone: 'Phone',
    additionalInfo: 'Additional Information',
    searching: 'Searching…',
    noAddressResults: 'No results'
  },
  fr: {
    shippingTitle: 'Détails de livraison',
    closeCart: 'Fermer le panier',
    firstName: 'Prénom',
    lastName: 'Nom',
    address: 'Adresse',
    address2: 'Adresse 2 (facultatif)',
    city: 'Ville',
    postalCode: 'Code Postal',
    provinceState: 'Province/État',
    phone: 'Téléphone',
    additionalInfo: 'Informations supplémentaires',
    searching: 'Recherche…',
    noAddressResults: 'Aucun résultat'
  },
  ca: {
    shippingTitle: 'Dades d\'Enviament',
    closeCart: 'Tancar cistella',
    firstName: 'Nom',
    lastName: 'Cognom',
    address: 'Adreça',
    address2: 'Adreça 2 (opcional)',
    city: 'Ciutat',
    postalCode: 'Codi Postal',
    provinceState: 'Província/Estat',
    phone: 'Telèfon',
    additionalInfo: 'Informació Addicional',
    searching: 'Cercant…',
    noAddressResults: 'Sense resultats'
  },
  de: {
    shippingTitle: 'Lieferdetails',
    closeCart: 'Warenkorb schließen',
    firstName: 'Vorname',
    lastName: 'Nachname',
    address: 'Adresse',
    address2: 'Adresse 2 (optional)',
    city: 'Stadt',
    postalCode: 'Postleitzahl',
    provinceState: 'Bundesland',
    phone: 'Telefon',
    additionalInfo: 'Zusätzliche Informationen',
    searching: 'Suche…',
    noAddressResults: 'Keine Ergebnisse'
  },
  eu: {
    shippingTitle: 'Bidalketa xehetasunak',
    closeCart: 'Itxi saskia',
    firstName: 'Izena',
    lastName: 'Abizena',
    address: 'Helbidea',
    address2: 'Helbidea 2 (aukerakoa)',
    city: 'Hiria',
    postalCode: 'Posta-kodea',
    provinceState: 'Probintzia',
    phone: 'Telefonoa',
    additionalInfo: 'Informazio gehigarria',
    searching: 'Bilatzen…',
    noAddressResults: 'Emaitzarik ez'
  }
}

const ShippingDetails = ({
  shippingDetails,
  setShippingDetails,
  toggleCart,
  errors,
  language,
  proximity
}: ShippingDetailsProps) => {
  const texts = translations[language] || translations.es

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setShippingDetails(prev => ({
      ...prev,
      [name]: value,
      // Any manual edit invalidates the previously selected coords so the
      // backend will re-geocode (or fail closed) instead of trusting stale
      // coordinates from a different address.
      ...(name === 'addressLine1' || name === 'city' || name === 'postalCode' || name === 'province'
        ? { coords: undefined }
        : {})
    }))
  }

  const handlePlaceSelect = (place: SelectedAddressPlace) => {
    const sa = place.structuredAddress || {}
    setShippingDetails(prev => ({
      ...prev,
      addressLine1: getAddressLabel(place),
      city: sa.locality || prev.city,
      postalCode: sa.postalCode || prev.postalCode,
      province: sa.region || prev.province,
      coords: sa.coordinates ?? prev.coords
    }))
  }

  return (
    <>
      {/* Encabezado similar al de CartList */}
      <div className='orderingCart__container__header'>
        <h2 className='orderingCart__container__header__title'>
          {texts.shippingTitle}
        </h2>
        <button
          onClick={toggleCart}
          aria-label={texts.closeCart}
          className='orderingCart__container__header__close'
        >
          <X size={20} />
        </button>
      </div>
      <div className='orderingCart__container__divider' />

      {/* Formulario completo de envío */}
      <div className='orderingCart__shippingDetails'>
        <div className='orderingCart__form-field'>
          <label className='orderingCart__form-label'>{texts.firstName}</label>
          <input
            type='text'
            name='firstName'
            autoComplete='shipping given-name'
            value={shippingDetails.firstName}
            onChange={handleChange}
            className={`orderingCart__form-input ${errors?.firstName ? 'orderingCart__form-input--error' : ''}`}
          />
          {errors?.firstName && <span className='orderingCart__form-error'>{errors.firstName}</span>}
        </div>

        <div className='orderingCart__form-field'>
          <label className='orderingCart__form-label'>{texts.lastName}</label>
          <input
            type='text'
            name='lastName'
            autoComplete='shipping family-name'
            value={shippingDetails.lastName}
            onChange={handleChange}
            className={`orderingCart__form-input ${errors?.lastName ? 'orderingCart__form-input--error' : ''}`}
          />
          {errors?.lastName && <span className='orderingCart__form-error'>{errors.lastName}</span>}
        </div>

        <div className='orderingCart__form-field'>
          <label className='orderingCart__form-label'>{texts.address}</label>
          <AddressAutocomplete
            id='shipping-address'
            name='addressLine1'
            value={shippingDetails.addressLine1}
            onChange={handleChange}
            onSelect={handlePlaceSelect}
            labels={{ loading: texts.searching, noResults: texts.noAddressResults }}
            locale={language || 'es'}
            proximity={proximity}
            placeholder={texts.address}
            autoComplete='shipping address-line1'
            inputClassName={`orderingCart__form-input ${errors?.addressLine1 ? 'orderingCart__form-input--error' : ''}`}
          />
          {errors?.addressLine1 && <span className='orderingCart__form-error'>{errors.addressLine1}</span>}
        </div>

        <div className='orderingCart__form-field'>
          <label className='orderingCart__form-label'>{texts.address2}</label>
          <input
            type='text'
            name='addressLine2'
            autoComplete='shipping address-line2'
            value={shippingDetails.addressLine2}
            onChange={handleChange}
            className='orderingCart__form-input'
          />
        </div>

        {/* Ciudad y Código Postal en la misma línea */}
        <div className='orderingCart__form-row'>
          <div className='orderingCart__form-field'>
            <label className='orderingCart__form-label'>{texts.city}</label>
            <input
              type='text'
              name='city'
              autoComplete='shipping address-level2'
              value={shippingDetails.city}
              onChange={handleChange}
              className={`orderingCart__form-input ${errors?.city ? 'orderingCart__form-input--error' : ''}`}
            />
            {errors?.city && <span className='orderingCart__form-error'>{errors.city}</span>}
          </div>
          <div className='orderingCart__form-field'>
            <label className='orderingCart__form-label'>{texts.postalCode}</label>
            <input
              type='text'
              name='postalCode'
              autoComplete='shipping postal-code'
              value={shippingDetails.postalCode}
              onChange={handleChange}
              className={`orderingCart__form-input ${errors?.postalCode ? 'orderingCart__form-input--error' : ''}`}
            />
            {errors?.postalCode && <span className='orderingCart__form-error'>{errors.postalCode}</span>}
          </div>
        </div>

        {/* Provincia/Estado y Teléfono en la misma línea */}
        <div className='orderingCart__form-row'>
          <div className='orderingCart__form-field'>
            <label className='orderingCart__form-label'>{texts.provinceState}</label>
            <input
              type='text'
              name='province'
              autoComplete='shipping address-level1'
              value={shippingDetails.province}
              onChange={handleChange}
              className={`orderingCart__form-input ${errors?.province ? 'orderingCart__form-input--error' : ''}`}
            />
            {errors?.province && <span className='orderingCart__form-error'>{errors.province}</span>}
          </div>
          <div className='orderingCart__form-field'>
            <label className='orderingCart__form-label'>{texts.phone}</label>
            <input
              type='tel'
              name='phone'
              autoComplete='shipping tel'
              value={shippingDetails.phone}
              onChange={handleChange}
              className={`orderingCart__form-input ${errors?.phone ? 'orderingCart__form-input--error' : ''}`}
            />
            {errors?.phone && <span className='orderingCart__form-error'>{errors.phone}</span>}
          </div>
        </div>

        <div className='orderingCart__form-field'>
          <label className='orderingCart__form-label'>{texts.additionalInfo}</label>
          <textarea
            name='additionalInfo'
            value={shippingDetails.additionalInfo}
            onChange={handleChange as any}
            rows={3}
            className='orderingCart__form-textarea'
          />
        </div>
      </div>
    </>
  )
}

export default ShippingDetails
